import { pushEvent } from "helpers/utils/checkoutDataLayer";
import { events, NO_OTHER_METHOD, NO_SAVED_METHOD, NEW_PAYMENT_METHODS_DATALAYER } from "constants/dataLayer";
import { deviceTypes, SAVED_DETAIL, FRESH_PAYMENT } from "constants/dataLayer";
import { SEMI_CLOSED_WALLET_STATES, WALLET_STATES } from "./constants";
import { PAYMENT_FLOWS, PAYMENT_METHOD_MAPPING } from "constants/payment";
import { DEVICE_TYPE, NOT_AVAILABLE, PAGE_NAMES, PAGE_TYPES } from "constants/index";
import { getBucketSize } from "helpers/utils/getBucketSize";
import { getPaymentOffers } from "layouts/Payment/common/dependencies/dependencyhelpers";
import { logger } from "logging";
import { PAYMENT_SCREENS } from "constants/paymentScreens";
import { isServer } from "helpers/utils/isServer";


const currentPageName = "PaymentPage";

const desktopPaymentPage = "DesktopPayment";

interface PayNowEventType {
  paymentMode: string,
  hasOffers: boolean,
  event: string,
  walletAmount: number,
  payment_applied_offers: string,
  isSavedMethod: boolean,
  mode_of_payment: string,
  paymentMethod: string,
  interaction_location: string,
  upi_payment_app: string,
  codCharge?: string,
  network_type? : string,
  type_of_payment?: string;
  sub_mode_of_payment?: string;
}

interface ITrackPayNowClick{
  deviceType: string;
  paymentMethod: string;
  paymentMode: string;
  hasOffers?: boolean;
  isSaved?: boolean;
  selectedUpi?: string;
  walletAmount: number;
  giftcardAmount?: number;
  appliedOfferRuleId?: string;
  intentApp?: string;
  codCharge?: string;
  paymentScreen?: string;
  currentinteractionLocation?: string;
}

interface OtherPaymentClickEventType {
  pageName: string,
  noOfOffersVisible: number,
  isOfferVisible: boolean,
}
interface ErrorPageLoadsEventType {
  error_code: number|string,
  error_message: string,
  interaction_location: string,
}

interface ITrackAutoOrderConfirmation{
  page: string;
  page_type: string;
  source_platform: string;
  payment_method_failed: string;  
  is_auto_order: boolean;
  is_logged_in: boolean;
}

interface ITrackAutoOrderConfirmationBottomSheet{
  source_platform: string;
  payment_method_failed: string;  
}

const pushPayNow = (data: PayNowEventType) => {
  pushEvent(events.PAYMENT_PAY_NOW, data);
};

const pushOtherPaymentClick = (data: OtherPaymentClickEventType) => {
  pushEvent(events.OTHER_PAYMENT_CLICK, data);
};

export const trackBankCardValidForOffer = () => {
  const data = {
    isBankCardValidForOffer: 1,
  };
  pushEvent(events.TRACK_BANK_CARD_VALID_FOR_OFFER, data);
};

export const trackBankCardBinInvalid = () => {
  const data = {
    isBankCardBinInvalid: 1,
  };
  pushEvent(events.TRACK_BANK_CARD_BIN_INVALID, data);
};

export const trackErrorPageLoad = (data: ErrorPageLoadsEventType) => {
  pushEvent(events.ERROR_PAGE_LOADS, data);
};

export const trackErrorInlineMessage = (data: ErrorPageLoadsEventType) => {
  pushEvent(events.ERROR_INLINE_MESSAGE, data);
};

/**
 * @description
 * tracking function of PayNow Click
 * builds params for pushPayNow event
 * @param deviceType - type of device
 * @param paymentMethod - method of payment
 * @param hasOffers - payment mode has offers or not
 * @param isSaved - on saved payment or not
 * @param selectedUpi - send into tracking if user has selected Upi vpa from dropdown and clicked pay now button
 */
export const trackPayNowClick = ({
  deviceType,
  paymentMethod,
  hasOffers,
  isSaved = false,
  selectedUpi = "",
  walletAmount = 0,
  giftcardAmount = 0,
  appliedOfferRuleId,
  intentApp = "",
  codCharge,
  paymentScreen,
  currentinteractionLocation,
  paymentMode,
}: ITrackPayNowClick) => {
  const device = deviceType === DEVICE_TYPE.MOBILE ? deviceTypes.MSITE : deviceTypes.DESKTOP;
  const paymentType = isSaved ? SAVED_DETAIL : FRESH_PAYMENT;
  let interactionLocation = PAGE_NAMES.FRESH_PAYMENT_PAGE;
  let type = "fresh";
  let mode = paymentMode || NOT_AVAILABLE;

  if (walletAmount && mode !== NEW_PAYMENT_METHODS_DATALAYER.WALLET) {
    mode = NEW_PAYMENT_METHODS_DATALAYER.WALLET + "_X_" + mode;
  }

  if (giftcardAmount && mode !== NEW_PAYMENT_METHODS_DATALAYER.GIFT_CARD) {
   mode = NEW_PAYMENT_METHODS_DATALAYER.GIFT_CARD + "_X_" + mode;
  }


  if (isSaved || paymentScreen == PAYMENT_SCREENS.SAVED) {
    interactionLocation = PAGE_NAMES.SAVED_PAYMENT_PAGE;
    type = "saved";
  }
  /**
   * if we pass currentinteractionLocation then the interactionLocation
   * value will be overwritten else it will take the saved or new
   */
  if (currentinteractionLocation) {
    interactionLocation = currentinteractionLocation;
  }

  const payNowEventObj: PayNowEventType = {
    event: "PAYMENT_PAY_NOW",
    paymentMode: selectedUpi
      ? `${device}:${paymentType}:${paymentMethod}:UPI_${selectedUpi}`
      : `${device}:${paymentType}:${paymentMethod}`,
    hasOffers: !!hasOffers,
    walletAmount: walletAmount,
    payment_applied_offers: appliedOfferRuleId ? appliedOfferRuleId.toString() : "",
    isSavedMethod: isSaved || false,
    paymentMethod: paymentMethod || NOT_AVAILABLE,
    mode_of_payment: mode,
    interaction_location: interactionLocation,
    upi_payment_app: intentApp,
    network_type: (navigator as any)?.connection?.effectiveType || "",
    type_of_payment: type,
    sub_mode_of_payment: selectedUpi,
  };

  /**
   * Send cod charge only for cod Payment method.
   */
  if (codCharge) {
    payNowEventObj.codCharge = codCharge;
  } else {
    payNowEventObj.codCharge = "0";
  }

  pushPayNow(payNowEventObj);
};

/**
 * @description
 * tracking function for other payments click
 * builds params for other payment click
 * @param deviceType - type of device
 * @param offersLength - required to determine no of offers visible
 */
export const trackOtherPaymentsClick = (deviceType:string, offersLength:number, flow = PAYMENT_FLOWS.CHECKOUT) => {
  const device = deviceType === DEVICE_TYPE.MOBILE ? deviceTypes.MSITE : deviceTypes.DESKTOP;
  /** pageName will be different for checkout, wallet and giftcard flow */
  let pageName = `${device}:PaymentPage`;
  if (flow === PAYMENT_FLOWS.WALLET) {
    pageName = `${device}:Wallet:PaymentPage`;
  } else if (flow === PAYMENT_FLOWS.GIFT_CARD) {
    pageName = `${device}:GC:PaymentPage`;
  }
  const noOfOffersVisible = offersLength;
  const isOfferVisible = !!noOfOffersVisible;
  pushOtherPaymentClick({
    pageName,
    noOfOffersVisible,
    isOfferVisible,
  });
};


export const trackWalletOtpLoad = () => {
  const pageName = `${deviceTypes.MSITE}:walletOTP`;
  if (!isServer()) {
    if (window.datalayer) {
      delete window.datalayer.walletOTPInteractions;
    }
   }
  pushEvent(events.WALLET_OTP_LOAD, {
    pageName,
    walletOTPinitiate: 1,
  });
};

export const trackWalletOtpInteractions = (status: string) => {
  const walletOTPInteractions = `walletOTP${status}`;
  pushEvent(events.WALLET_OTP_INTERACTIONS, {
    walletOTPInteractions,
  });
};

/**
 * @description
 * tracking addNewAddress redirection if address DTO is null in cart refresh
 */
export const trackAddNewAddressRedirectionForNewUsers = () => {
  pushEvent(events.ADD_NEW_ADDRESS_REDIRECTION, {
    addNewAddressRedirectionForNewUsers: true,
  });
};

export const trackDesktopPaymentPageLoad = (params: any) => {
  const {
    isGpayIntentAvailable,
    ABexperiment,
    savedPaymentMethodsAvailable = NO_SAVED_METHOD,
    discountApplied,
    cartItemCount,
    cartProductCount,
    WidgetExperiment,
    isScWalletEnabled,
    verificationRequired,
    zestExperiment,
    isNewShippingAddressWidget,
    ab_Edd_checkout,
    otherPaymentMethodsAvailable = NO_OTHER_METHOD,
    cartShipping,
    cartSubTotal,
    cartDiscount,
    cartTotal,
    flow,
    currency,
    offersData,
    cartCouponDiscount, // Required for retina
    codCharge,
  } = params;
  /** pageName will be different for checkout, wallet and giftcard flow */
  let pageName = "nykaa:paymentPage1";
  if (flow === PAYMENT_FLOWS.WALLET) {
    pageName = "nykaa:Wallet:paymentPage1";
  } else if (flow === PAYMENT_FLOWS.GIFT_CARD) {
    pageName = "nykaa:GC:paymentPage1";
  } else if (flow === PAYMENT_FLOWS.COD_TO_PREPAID) {
    pageName = "mWeb:codToPrepaidPaymentPage1";
  }
  const walletSession = verificationRequired ? SEMI_CLOSED_WALLET_STATES.NOT_VERIFIED : SEMI_CLOSED_WALLET_STATES.VERIFIED;
  const walletType = isScWalletEnabled ? WALLET_STATES.SEMI_CLOSED : WALLET_STATES.CLOSED;
  const page = PAGE_NAMES.DESKTOP_PAYMENT_PAGE;
  const page_type = PAGE_TYPES.PAYMENT_PAGE;
  const cart_value_bucket = getBucketSize(cartTotal || 0);
  const payment_available_offers = getPaymentOffers(offersData?.paymentRules);

  pushEvent(events.DESKTOP_PAYMENT_PAGE_LOAD, {
    page,
    page_type,
    currency,
    pageName,
    isGpayIntentAvailable,
    ABexperiment,
    savedPaymentMethodsAvailable,
    discountApplied,
    cartItemCount,
    cartProductCount,
    WidgetExperiment,
    walletSession,
    walletType,
    zestExperiment,
    newShippingAddressWidgetExperiment: isNewShippingAddressWidget ? "variant1" : "default",
    ab_Edd_checkout,
    otherPaymentMethodsAvailable,
    cartShipping,
    cartSubTotal,
    cartDiscount,
    cartTotal,
    cart_value_bucket,
    payment_available_offers,
    cartCouponDiscount,
    pageType: pageName,
    codCharge,
  });
};

export const trackPayment1PageLoad = (params: any) => {
  const {
    page,
    page_type,
    currency,
    cart_value_bucket,
    isGpayIntentAvailable,
    ABexperiment,
    savedPaymentMethodsAvailable = NO_SAVED_METHOD,
    discountApplied,
    cartItemCount,
    cartProductCount,
    WidgetExperiment,
    isScWalletEnabled,
    verificationRequired,
    zestExperiment,
    isNewShippingAddressWidget,
    ab_Edd_checkout,
    cartShipping,
    cartSubTotal,
    cartDiscount,
    cartTotal,
    cartCouponDiscount,
    products,
    flow,
    payment_available_offers,
    is_logged_in,
    price_detail_expanded,
    is_payment_offer_visible,
    is_payment_failure_enabled,
    isFirstLandingPageAfterCheckout,
    is_new_upi_intent,
    is_discount_info_strip_shown,
    isNewAddressDesignEnabled,
    isCodAvailable,
    isCodShown,
  } = params;
  /** pageName will be different for checkout, wallet and giftcard flow */
  let pageName = "nykaa:paymentPage1";
  if (flow === PAYMENT_FLOWS.WALLET) {
    pageName = "nykaa:Wallet:paymentPage1";
  } else if (flow === PAYMENT_FLOWS.GIFT_CARD) {
    pageName = "nykaa:GC:paymentPage1";
  } else if (flow === PAYMENT_FLOWS.COD_TO_PREPAID) {
    pageName = "mWeb:codToPrepaidPaymentPage1";
  }
  const walletSession = verificationRequired ? SEMI_CLOSED_WALLET_STATES.NOT_VERIFIED : SEMI_CLOSED_WALLET_STATES.VERIFIED;
  const walletType = isScWalletEnabled ? WALLET_STATES.SEMI_CLOSED : WALLET_STATES.CLOSED;

  pushEvent(events.PAYMENT1_PAGE_LOAD, {
    page,
    page_type,
    currency,
    cart_value_bucket,
    pageName,
    isGpayIntentAvailable,
    ABexperiment,
    savedPaymentMethodsAvailable,
    discountApplied,
    cartItemCount,
    cartProductCount,
    WidgetExperiment,
    walletSession,
    walletType,
    zestExperiment,
    newShippingAddressWidgetExperiment: isNewShippingAddressWidget ? "variant1" : "default",
    ab_Edd_checkout,
    cartShipping,
    cartSubTotal,
    cartDiscount,
    cartTotal,
    cartCouponDiscount,
    products,
    payment_available_offers,
    pageType: pageName,
    is_logged_in,
    price_detail_expanded,
    is_payment_offer_visible,
    is_payment_failure_enabled,
    isFirstLandingPageAfterCheckout,
    is_new_upi_intent,
    is_discount_info_strip_shown,
    isNewAddressDesignEnabled,
    isCodAvailable,
    isCodShown,
  });
};

export const trackGiftCardPaymentPageLoads = (params: any) => {
  const {
    is_logged_in,
    cartTotal,
  } = params;

  pushEvent(events.GIFT_CARD_PAYMENT_PAGE_LOAD, {
    is_logged_in,
    cartTotal,
  });
};

/**
 * This is used to set isFirstLandingPageAfterCheckout to false in dataLayer
 * after checkout success if fired once
 */
export const setCheckoutSuccessToFalseInDataLayer = () => {
  pushEvent(events.CHECKOUT_STATUS, {
    isFirstLandingPageAfterCheckout: false,
  });
};

export const trackPayment2PageLoad = (params: any) => {
  const {
    otherPaymentMethodsAvailable = NO_OTHER_METHOD,
    cartCouponDiscount,
    flow,
    walletAmount,
    page,
    page_type,
    price_detail_expanded,
    codCharge,
    is_new_upi_intent,
    is_payment_failure_enabled,
    is_discount_info_strip_shown,
    isNewAddressDesignEnabled,
    isCodAvailable,
    isCodShown,
  } = params;
  /** pageName will be different for checkout, wallet and giftcard flow */
  let pageName = "nykaa:paymentPage2";
  if (flow === PAYMENT_FLOWS.WALLET) {
    pageName = "nykaa:Wallet:paymentPage2";
  } else if (flow === PAYMENT_FLOWS.GIFT_CARD) {
    pageName = "nykaa:GC:paymentPage2";
  } else if (flow === PAYMENT_FLOWS.COD_TO_PREPAID) {
    pageName = "mWeb:codToPrepaidPaymentPage2";
  }

  pushEvent(events.PAYMENT2_PAGE_LOAD, {
    pageName,
    otherPaymentMethodsAvailable,
    cartCouponDiscount,
    walletAmount,
    pageType: pageName,
    page,
    page_type,
    price_detail_expanded,
    codCharge,
    is_new_upi_intent,
    is_payment_failure_enabled,
    is_discount_info_strip_shown,
    isNewAddressDesignEnabled,
    isCodAvailable,
    isCodShown,
  });
};

export const trackPaymentOfferWidgetClicked = (params: any) => {
  const { interactionLocation } = params;
  pushEvent(events.PAYMENT_OFFER_WIDGET_CLICK, {
    event: "PAYMENT_OFFER_WIDGET_CLICK",
    interaction_location: interactionLocation,
  });
};

export const trackRemoveCouponClick = (params: any) => {
  const { interactionLocation, couponCode } = params;
  pushEvent(events.REMOVE_COUPON_CLICK, {
    event: "REMOVE_COUPON_CLICK",
    interaction_location: interactionLocation,
    coupon_code: couponCode,
  });
};

export const trackKeepCouponClick = (params: any) => {
  const { interactionLocation } = params;
  pushEvent(events.KEEP_COUPON_CLICK, {
    event: "KEEP_COUPON_CLICK",
    interaction_location: interactionLocation,
  });
};

export const trackCheckoutExperiments = (experiments: any) => {
  pushEvent(events.CHECKOUT_EXPERIMENTS, {
    event: "CHECKOUT_EXPERIMENTS",
    ...experiments,
  });
};

export const trackPaymentOfferClicked = (params: any) => {
  const {
    interactionLocation,
    isOfferEligible,
    paymentOffer,
    paymentOfferAmount,
    paymentOfferMode,
  } = params;

  pushEvent(events.PAYMENT_OFFER_CLICKED, {
    event: "PAYMENT_OFFER_CLICKED",
    interaction_location: interactionLocation || "",
    is_offer_eligible: isOfferEligible || "",
    payment_offer: paymentOffer || "",
    payment_offer_amount: paymentOfferAmount || "",
    payment_offer_mode: paymentOfferMode || "",
  });
};

export const trackPaymentMethodClick = (params: any) => {
  let {
    deviceType,
    isSaved,
    paymentMethod,
    disableTracking = false,
    isLoggedIn,
    interactionLocation
  } = params;

  if (!paymentMethod) return;

  if (disableTracking) return;

  const device = deviceType === DEVICE_TYPE.MOBILE ? deviceTypes.MSITE : deviceTypes.DESKTOP;
  const paymentType = isSaved ? SAVED_DETAIL : FRESH_PAYMENT;
  if(!interactionLocation){
    interactionLocation = isSaved ? PAGE_NAMES.SAVED_PAYMENT_PAGE : PAGE_NAMES.FRESH_PAYMENT_PAGE;
  }
  const transformedMethod = PAYMENT_METHOD_MAPPING[paymentMethod];
  pushEvent(events.PAYMENT_METHOD_CLICK, {
    event: "PAYMENT_MODE_CLICK",
    paymentMode: `${device}:${paymentType}:${transformedMethod}`,
    paymentMethod: paymentMethod || NOT_AVAILABLE,
    is_logged_in: isLoggedIn,
    interaction_location: interactionLocation,
  });
};

export const trackPaymentFailure = (params: any) => {
  try {
    const {
      deviceType,
      isSaved = false,
      paymentMethod,
      error,
      errorMessage,
      errorType,
    } = params;
    const device = deviceType === DEVICE_TYPE.MOBILE ? deviceTypes.MSITE : deviceTypes.DESKTOP;
    const paymentType = isSaved ? SAVED_DETAIL : FRESH_PAYMENT;
    pushEvent(events.PAYMENT_PAY_NOW_FAILURE, {
      event: "PAYMENT_FAILED",
      paymentMode: `${device}:${paymentType}:${paymentMethod}`,
      paymentType: paymentType || NOT_AVAILABLE,
      paymentMethod: paymentMethod || NOT_AVAILABLE,
      errorType: errorType || NOT_AVAILABLE,
      apiEndpoint: error?.config?.url || NOT_AVAILABLE,
      errorMessage: errorMessage || error?.message,
    });
    trackErrorInlineMessage({
      error_code: error?.code || NOT_AVAILABLE,
      error_message: errorMessage || error?.message,
      interaction_location: `${PAGE_TYPES.PAYMENT_PAGE}:${paymentMethod}`,
    });
  } catch (error) {
    logger.error(error, "Error in trackPaymentFailure function");
  }
};

export const trackPaymentDetailClick = (pageName: string, isExpanded?: boolean) => {
  const interactionLocation = pageName === PAYMENT_SCREENS.SAVED ? PAGE_NAMES.SAVED_PAYMENT_PAGE : PAGE_NAMES.FRESH_PAYMENT_PAGE;
  const params = {
    event: "paymentDetailsClick",
    pageName,
    is_expanded: false,
    interaction_location: "",
  };
  if (isExpanded) {
    params.is_expanded = isExpanded;
  }
  if (interactionLocation) {
    params.interaction_location = interactionLocation;
  }
  pushEvent(events.TRACK_PAYMENT_DETAIL_CLICK, params);
};

export const trackInfoIconClick = (pageName: string, type: string) => {
  pushEvent(events.TRACK_PAYMENT_DETAIL_CLICK, {
    event: `${type}InfoIconClicked`,
    pageName,
  });
};


export const trackShippingAddressWidgetExperiment = (params: {isNewShippingAddressWidget: boolean}) => {
  const {
    isNewShippingAddressWidget,
  } = params;
  pushEvent(events.SHIPPING_ADDRESS_WIDGET_LOAD, {
    pageName: currentPageName,
    event: "changeAddressClicked",
    newShippingAddressWidgetExperiment: isNewShippingAddressWidget ? "variant1" : "default",
  });
};


export const trackUserConsentChecked = ({ isSavedPage, deviceType }:{ isSavedPage: boolean, deviceType: string}) => {
  let pageName;
  let event;
  if ( deviceType === "MSITE") {
    pageName = isSavedPage ? "nykaa:paymentPage1" : "nykaa:paymentPage2";
    event = isSavedPage ? "saveCardCheckedSavedPaymentPage" : "saveCardCheckedNewPayPage";
    pushEvent(events.USER_CONSENT_CHECKBOX_CHECKED, {
      pageName,
      event,
    });
  } else if ( deviceType === "WEBSITE" ) {
    pageName = isSavedPage && desktopPaymentPage;
    event = isSavedPage && "saveCardCheckedSavedPaymentPage";
    pushEvent(events.USER_CONSENT_CHECKBOX_CHECKED, {
      pageName,
      event,
    });
  }
};

export const trackCheckButtonClickOnToast = () => {
  pushEvent(events.CHECK_CLICKED_ON_DELIVEERY_TOAST, {
    pageName: currentPageName,
    toastUpdatedDeliveryDetails: "1",
  });
};

export const trackWidgetDropDownClick = () => {
  pushEvent(events.EDD_WIDGET_DROPDOWN_CLICK, {
    pageName: currentPageName,
    deliveryDetailsDropDown: "1",
  });
};

export const trackKnowMoreClicked = ({ isSavedPage, deviceType }:{ isSavedPage: boolean, deviceType: string }) => {
  let pageName;
  let event;
  if ( deviceType === "MSITE" ) {
    pageName = isSavedPage ? "nykaa:paymentPage1" : "nykaa:paymentPage2";
    event = isSavedPage ? "knowMoreClickedSavedPayPage" : "knowMoreClickedNewPayPage";
    pushEvent(events.USER_CONSENT_KNOW_MORE_CLICKED, {
      pageName,
      event,
    });
  } else if ( deviceType === "WEBSITE") {
    pageName = desktopPaymentPage;
    event = "knowMoreClickedDesktopSavedPayPage";
    pushEvent(events.USER_CONSENT_KNOW_MORE_CLICKED, {
      pageName,
      event,
    });
  }
};

export const trackConsentButtonClicked = ({ isSavedPage, isSecured, deviceType }:{ isSavedPage: boolean, isSecured: boolean, deviceType: string}) => {
  let pageName;
  let event;
  if (deviceType === "MSITE") {
    pageName = isSavedPage ? "nykaa:paymentPage1" : "nykaa:paymentPage2";
    if (isSavedPage) {
      event = isSecured ? "secureAndPayClickedSavedPaymentPage" : "payWithoutSecuringTheCardSavedPaymentPage";
    } else {
      event = isSecured ? "secureAndPayClickedNewPaymentPage" : "payWithoutSecuringTheCardNewPaymentPage";
    }
    pushEvent(events.USER_CONSENT_BUTTON_CLICKED, {
      pageName,
      event,
    });
  } else if ( deviceType === "WEBSITE") {
    pageName = isSavedPage && desktopPaymentPage;
    if (isSavedPage) {
      event = isSecured ? "secureAndPayClickedSavedPaymentPage" : "payWithoutSecuringTheCardSavedPaymentPage";
    }
    pushEvent(events.USER_CONSENT_BUTTON_CLICKED, {
      pageName,
      event,
    });
  }
};

export const trackCodNudgeVisible = () => {
  let event = "nykaa.cod.payNudgeDisplayed";
  pushEvent(events.COD_NUDGE_VISIBLE, {
    pageName: currentPageName,
    event,
  });
};

export const trackCODNudgeCtaClick = () => {
  let event = "nykaa.cod.payNudgeClicked";
  pushEvent(events.COD_NUDGE_VISIBLE, {
    pageName: currentPageName,
    event,
  });
};

/**
 * function to track the backfriction bottomsheet or modal render
 */
 export const trackBackFrictionScreenRender = () => {
  pushEvent(events.BACK_FRICTION_SCREEN_RENDER, {});
};

/**
 * @description -- function to track click Continue with Payment button on backfriction modal or bottomsheet
 */
export const trackContinuewithPaymentClick = () => {
  pushEvent(events.BACK_FRICTION_CONTINUE_WITH_PAYMENT, {});
};

/**
 * @description -- function to track click Cancel Payment button on backfriction modal or bottomsheet
 */
export const trackCancelPaymentClick = () => {
  pushEvent(events.BACK_FRICTION_CANCEL_PAYMENT, {});
};

/**
 * @description -- function to track status of payment on postpayment
 * @param deviceType -string
 */
export const trackPaymentStatusPostPayment = (params: any) => {
  try {
    const {
      statusValue,
      paymentGateway,
      error,
    } = params;

    pushEvent(events.POST_PAYMENT_STATUS, {
      event: "POST_PAYMENT_STATUS",
      statusValue,
      paymentGateway,
      errorMessage: error?.message,
    });
  } catch (error) {
    logger.error(error);
    console.log("error in trackPaymentStatusPostPayment", error);
  }
};

/**
 * @description -- function to track payment success on postpayment
 * @param deviceType -string
 */
export const trackPostPaymentSuccess = (params: any) => {
  const {
    paymentMode,
    orderId,
    paymentGateway,
    isLoggedIn,
    paymentOfferApplied,
  } = params;
  pushEvent(events.POST_PAYMENT_SUCCESS, {
    event: events.POST_PAYMENT_SUCCESS,
    paymentMode,
    orderId,
    paymentGateway,
    is_logged_in: isLoggedIn,
    payment_offer_availed: paymentOfferApplied,
  });
};

/**
* @description -- function to track payment faliure on postpayment
*/
export const trackPostPaymentFaliure = (params: any) => {
  const {
    paymentMode,
    orderId,
    paymentGateway,
    isLoggedIn,
  } = params;
    pushEvent(events.POST_PAYMENT_FALIURE, {
      event: events.POST_PAYMENT_FALIURE,
      paymentMode,
      orderId,
      paymentGateway,
      is_logged_in: isLoggedIn,
    });
};

/**
* @description -- function to track back button click on payment page 2
* Should be fired only when user is going back to payment page 1 as we are using this to track payment page 1 visit
*/
export const trackBackButtonClickToSavedPaymentPage = () => {
    pushEvent(events.PAYMENT_PAGE_BACK_BUTTON_CLICK, {
      event: events.PAYMENT_PAGE_BACK_BUTTON_CLICK,
    });
};

/**
* @description -- this function is used to track memory leak cases for apps
*/
export const trackMemoryLeakCase = () => {
  pushEvent(events.MEMORY_LEAK, {
    event: events.MEMORY_LEAK,
  });
};

/**
* @description -- function to track payment page 2 visit on click of Proceed to pay
* Should be fired only when user is going to payment page 2 as we are using this to track payment page 2 visit
*/
export const trackProceedToPayClick = () => {
  pushEvent(events.PROCEED_TO_PAY, {
    event: events.PROCEED_TO_PAY,
  });
};


/**
* @description -- function to track payment page 2 visit when user is directly sent to payment page 2
* This event is fired when when RedirectToPaymentScreen2.switchEnabled is true and user does not have any saved method
*/
export const trackDirectlyRedirectUserToNewPaymentPage = () => {
  pushEvent(events.AUTO_REDIRECT_TO_PAYMENT2_PAGE, {
    event: events.AUTO_REDIRECT_TO_PAYMENT2_PAGE,
  });
};

/**
* @description -- function to track app callback faliure
*/
export const trackAppCallbackFailed = (params: any) => {
  try {
    const {
      error,
      callbackName,
    } = params;
    pushEvent(events.APP_CALLBACK_FAILED, {
      event: events.APP_CALLBACK_FAILED,
      error,
      callbackName,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackAppCallbackFailed function");
  }
};

/**
* @description -- function to track app callback success
*/
export const trackAppCallbackSuccess = (params: any) => {
  try {
    const {
      callbackName,
    } = params;
    pushEvent(events.APP_CALLBACK_SUCCESS, {
      event: events.APP_CALLBACK_SUCCESS,
      callbackName,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackAppCallbackSuccess function");
  }
};


/**
* @description -- function to track event when customer id from app and web are not same
*/
export const trackCustomerIdMisMatched = (params: any) => {
  try {
    const {
      webCustomerId,
      appCustomerId,
    } = params;
    pushEvent(events.CUSTOMER_ID_MISMATCHED, {
      event: events.CUSTOMER_ID_MISMATCHED,
      webCustomerId,
      appCustomerId,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackCustomerIdMisMatched function");
  }
};

/**
* @description -- function to track cart not found
*/
export const trackCartNotFound = (params: any) => {
  try {
    const {
      customerId,
      errorMessage,
    } = params;
    pushEvent(events.CART_NOT_FOUND, {
      event: events.CART_NOT_FOUND,
      customerId,
      errorMessage,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackCartNotFound function");
  }
};


/**
* @description -- function to track payment failure bottomsheet load
*/
export const trackPaymentFailureBottomSheetLoad = (params: {
  event: string;
  masterPaymentStatus: string;
  paymentFallbackOptions: boolean;
  cod_charges_applied: boolean;
  cod_charge: string
}) => {
  try {
    const {
      event,
      masterPaymentStatus,
      paymentFallbackOptions,
      cod_charges_applied,
      cod_charge,
    } = params;

    pushEvent(event, {
      event,
      paymentFailureStatus: masterPaymentStatus,
      paymentFallbackOptionShown: paymentFallbackOptions,
      cod_charges_applied,
      cod_charge,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackPaymentFailureBottomSheetLoad function");
  }
};
/**
* @description -- function to track rendered saved methods
*/
export const trackRenderedSavedMethods = (params: { allSavedPaymentMethods: string[] }) => {
  try {
    const { allSavedPaymentMethods } = params;
    pushEvent(events.SAVED_PAYMENT_METHODS, {
      event: events.SAVED_PAYMENT_METHODS,
      renderedSavedPaymentMethods: allSavedPaymentMethods,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackRenderedSavedMethods function");
  }
};

/**
* @description -- function to track payment failure bottomsheet actions
* using same function for other payment click, cross button click, retry payments again click
*/
export const trackPaymentFailureBottomSheetActions = (params: {event: string}) => {
  try {
    const { event } = params;
    pushEvent(event, { event });
  } catch (error) {
    logger.error(error as Error, "Error in trackPaymentFailureBottomSheetActions function");
  }
};
 /*
 * @description -- function to track rendered fresh payment methods
 */
export const trackRenderedFreshMethods = (params: { allPaymentMethods: string[] }) => {
  try {
    const { allPaymentMethods } = params;
    pushEvent(events.FRESH_PAYMENT_METHODS, {
      event: events.FRESH_PAYMENT_METHODS,
      renderedFreshPaymentMethods: allPaymentMethods,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackRenderedFreshMethods function");
  }
};

/**
 * @description -- function to track order created event after order/create api success
 */
 export const trackOrderCreated = (params: { paymentMethod: string, paymentGateway: string }) => {
  try {
    const { paymentMethod, paymentGateway } = params;
    pushEvent(events.WEBVIEW_ORDER_CREATED, {
      event: events.WEBVIEW_ORDER_CREATED,
      paymentMethod,
      paymentGateway,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackOrderCreated function");
  }
};

/**
 * @description -- function to track paytm add money
 */
 export const trackPaytmAddMoney = () => {
  try {
    pushEvent(events.ADD_MONEY_PAYTM, {
      event: events.ADD_MONEY_PAYTM,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackPaytmAddMoney function");
  }
};

/**
 * @description -- function to track error popup rendering along with message and type.
 */
export const trackErrorModalOpens = (params: {title: string, message: string}) => {
  try {
    const { title, message } = params;
    pushEvent(events.PAYMENT_ERROR_POPUP, {
      event: events.PAYMENT_ERROR_POPUP,
      error_message: message,
      error_type: title,
    });
  } catch (e) {
    logger.error(e as Error, "Error in trackErrorModalOpens function");
  }
};

/**
 * @description -- function to track error popup clicks.
 */
export const trackErrorModalClick = () => {
  try {
    pushEvent(events.PAYMENT_ERROR_POPUP_CLICK, {
      event: events.PAYMENT_ERROR_POPUP_CLICK,
    });
  } catch (e) {
    logger.error(e as Error, "Error in trackErrorModalClick function");
  }
};

/**
 * @description -- function to track web view tab active on visiblity change
 */
 export const trackWebviewTabActive = () => {
  try {
    pushEvent(events.WEBVIEW_TAB_ACTIVE, {
      event: events.WEBVIEW_TAB_ACTIVE,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackWebviewTabActive function");
  }
};

/**
 * @description -- function to track web view tab in active on visiblity change
 */
 export const trackWebviewTabInActive = () => {
  try {
    pushEvent(events.WEBVIEW_TAB_INACTIVE, {
      event: events.WEBVIEW_TAB_INACTIVE,
    });
  } catch (error) {
    logger.error(error as Error, "Error in trackWebviewTabInActive function");
  }
};

/**
 * @description -- function to track error for upi intent flow on mweb.
 */
 export const trackUpiIntentErrorMweb = (params: {title: string, message: string, razorPayOrderId: string}) => {
  try {
    const { title, message, razorPayOrderId } = params;
    pushEvent(events.UPI_INTENT_ERROR_MWEB, {
      event: events.UPI_INTENT_ERROR_MWEB,
      error_message: message,
      error_type: title,
      razorPayOrderId: razorPayOrderId,
    });
  } catch (e) {
    logger.error(e as Error, "Error in trackUpiIntentErrorMweb function");
  }
};

/* @description -- function to track addressBackfriction loads
*/
export const trackAddressBackFrictionLoads = () => {
  pushEvent(events.ADDRESS_BACK_BOTTOMSHEET_LOADS, {});
};

/**
 * @description -- function to track addressBackfriction goToCart clicked
 */
export const trackAddressBackFrictionGoToCartClick = () => {
  pushEvent(events.ADDRESS_BACK_BOTTOMSHEET_CLICK, {
    interaction_location: "goToCart",
  });
};

/**
 * @description -- function to track addressBackfriction continue clicked
 */
export const trackAddressBackFrictioncontinueClick = () => {
  pushEvent(events.ADDRESS_BACK_BOTTOMSHEET_CLICK, {
    interaction_location: "continue",
  });
};

/* @description -- function to track addressBackfriction loads
*/
export const trackUpiIntentCancel = () => {
  pushEvent(events.UPI_INTENT_CANCEL_CLICK, {});
};

export const getInteractionLocation = (flow: string) => {
  let interactionLocation = "";

  switch (flow) {
    case PAYMENT_FLOWS.CODTOPREPAID:
      interactionLocation = "COD to prepaid";
      break;
    case PAYMENT_FLOWS.WALLET:
      interactionLocation = "wallet";
      break;
    case PAYMENT_FLOWS.GIFT_CARD:
      interactionLocation = "GC";
      break;
    default:
      break;
  }

  return interactionLocation;
};

/**
 * @description -- function to track click Cancel Payment button on upi timer modal
 */
 export const trackUpiCancelPaymentClick = (isQrFlow: boolean) => {
  pushEvent(events.UPI_CANCEL_PAYMENT_CLICK, {
    isQrFlow,
  });
};

/**
 * @description -- function to track action taken on cancel upi pop up
 */
 export const trackCancelPopUpAction = (isQrFlow: boolean, interactionLocation: string) => {
  pushEvent(events.CANCEL_UPI_POP_UP_ACTION, {
    isQrFlow,
    interactionLocation,
  });
};

/**
 * @description -- function to track generate new QR Code Click on qr code scan pop up
 */
 export const trackGenerateNewQRCode = () => {
  pushEvent(events.QR_GENERATE_NEW, {});
};

/**
 * @description -- function to track choose another method click on qr code scan pop up
 */
 export const trackChooseAnotherMethodClick = () => {
  pushEvent(events.QR_CHOOSE_ANOTHER_METHOD, {});
};

/**
 * @description -- function to track expriry of qr code
 */
 export const trackQrExpired = () => {
  pushEvent(events.QR_EXPIRED, {});
};
 export const trackChangeOrAddAddressClick = (params: any) => {
  const { url } = params;
  pushEvent(events.CHANGE_OR_ADD_ADDRESS_CLICKED, {
    url,
  });
};

export const trackPreConfirmationLoad = ({
  pay_using_cod_shown,
  pay_online_shown,
  ui_type,
}:any) => {
  pushEvent(events.PRECONFIRMATION_LOAD, {
    pay_using_cod_shown,
    pay_online_shown,
    ui_type,
  });
};

export const trackPreConfirmationRetryPaymentClick = ({
  interaction_location,
}:any) => {
  pushEvent(events.PRECONFIRMATION_RETRY_CLICK, {
    interaction_location,
  });
};

/**
 * Tracks the time and pageName when the payment page has fully loaded.
 *
 * This function pushes an event to the tracking system with the time it took to load the complete
 * payment page inclujding the api calls. This time is in ms.
 *
 * Event Name: PAYMENT_PAGE_LOADED_TIME
 */
 export const trackPaymentPageLoadTime = (time: number, apiRt: any, pageName: string) => {
   pushEvent(events.PAYMENT_PAGE_LOADED_TIME, {
     paymentPageLoadTime: time,
     checkoutPageName: pageName,
     apiRt,
   });
 };

 /**
 * Tracks the retry click event when user click retry on getValut API failure.
 *
 * Event Name: RETRY_WALLET_CLICK
 */
  export const trackRetryWalletClick = () => {
    pushEvent(events.RETRY_WALLET_CLICK, {
    });
  };

  /**
   * tracks auto order confirmation page
   */
  export const trackAutoOrderConfirmaton = ({
    page,
    page_type,
    source_platform,
    payment_method_failed,
    is_auto_order,
    is_logged_in
  }: ITrackAutoOrderConfirmation) => {

    pushEvent(events.AUTO_ORDER_CONFIRMATION_PAGE, {
      page,
      page_type,
      source_platform,
      payment_method_failed,
      is_auto_order,
      is_logged_in
    });
    
  };

  /**
   * tracks auto order confirmation bottomsheet
   */
  export const trackAutoOrderConfirmatonBottomsheet = ({
    source_platform,
    payment_method_failed,
  }: ITrackAutoOrderConfirmationBottomSheet) => {
    pushEvent(events.AUTO_ORDER_CONFIRMATION_BOTTOMSHEET, {
      source_platform,
      payment_method_failed,
    });
  };  

  /**
   * tracks auto order cancel click 
   */
  export const trackAutoOrderConfirmationCancelClick = () => {
    pushEvent(events.AUTO_ORDER_CANCEL_BTN_CLICKED, {});
  };  

  /**
   * tracks auto order confirm click 
   */
  export const trackAutoOrderConfirmClick = () => {
    pushEvent(events.AUTO_ORDER_CONFIRM_BTN_CLICKED, {});
  };    